import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * <af-selected-config> — "Selected configuration" summary box.
 * On variant:update, swaps its content for the freshly rendered copy of itself
 * carried in the event's section fragment, so the text is always server-rendered.
 */
class AfSelectedConfig extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      (event) => {
        const next = event.detail?.data?.html?.querySelector('af-selected-config');
        if (next) this.replaceChildren(...next.cloneNode(true).childNodes);
      },
      { signal: this.#abortController.signal }
    );
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }
}

/**
 * <af-live-total data-unit-price data-money-format data-currency> — order total.
 * Total = unit price (minor units) × current quantity-input value. The unit
 * price is refreshed from the variant:update fragment; quantity comes from the
 * section's quantity input (also re-read after variant switches, because
 * updateConstraints may reset the input without firing a quantity event).
 */
class AfLiveTotal extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      (event) => {
        const next = event.detail?.data?.html?.querySelector('af-live-total');
        if (next?.dataset.unitPrice) this.dataset.unitPrice = next.dataset.unitPrice;
        this.#render();
      },
      { signal }
    );
    target?.addEventListener(ThemeEvents.quantitySelectorUpdate, () => this.#render(), { signal });

    this.#render();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #render() {
    const out = this.querySelector('[data-total]');
    const unitPrice = Number(this.dataset.unitPrice);
    if (!out || !Number.isFinite(unitPrice)) return;

    const input = this.closest('.shopify-section, dialog')?.querySelector(
      'quantity-selector-component input[ref="quantityInput"]'
    );
    const quantity = Math.max(1, parseInt(input?.value ?? '1', 10) || 1);

    out.textContent = formatMoney(
      unitPrice * quantity,
      this.dataset.moneyFormat || '{{amount}}',
      this.dataset.currency || 'CAD'
    );
  }
}

/**
 * <af-variant-refresh data-key="..."> — generic wrapper that re-renders its
 * children from the matching element in the variant:update section fragment.
 * Used by blocks whose content depends on the selected variant but that the
 * base product-form morph doesn't cover (e.g. the specifications block's
 * actual-size row).
 */
class AfVariantRefresh extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      (event) => {
        const next = event.detail?.data?.html?.querySelector(
          `af-variant-refresh[data-key="${this.dataset.key}"]`
        );
        if (next) this.replaceChildren(...next.cloneNode(true).childNodes);
      },
      { signal: this.#abortController.signal }
    );
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }
}

if (!customElements.get('af-variant-refresh')) {
  customElements.define('af-variant-refresh', AfVariantRefresh);
}
if (!customElements.get('af-selected-config')) {
  customElements.define('af-selected-config', AfSelectedConfig);
}
if (!customElements.get('af-live-total')) {
  customElements.define('af-live-total', AfLiveTotal);
}
